import React from "react";
import SettingsModal from "../reusables/UseSettingsModal";

const InputSettingsDropdown = ({
  handleSettingsClick,
  handleAdditionClick,
  handleDeductionClick,
  showSettingsModal,
  handleCloseModal,
  setOrder,
  order,
}) => {
  const [showWidthHeightModal, setShowWidthHeightModal] = React.useState(false);
  const [unit, setUnit] = React.useState("inches");
  const [width, setWidth] = React.useState("");
  const [height, setHeight] = React.useState("");

  const handleWidthHeightClick = () => {
    setShowWidthHeightModal(true);
  };

  const handleCloseWidthHeightModal = () => {
    setShowWidthHeightModal(false);
  };

  const handleUnitChange = (e) => {
    setUnit(e.target.value);
  };

  const handleWidthChange = (e) => {
    setWidth(e.target.value);
  };

  const handleHeightChange = (e) => {
    setHeight(e.target.value);
  };

  const convertToFraction = (decimal) => {
    if (decimal >= 0.9375) return ""; 
    const fractions = [
      { fraction: "1/8", value: 0.125 },
      { fraction: "1/4", value: 0.25 },
      { fraction: "3/8", value: 0.375 },
      { fraction: "1/2", value: 0.5 },
      { fraction: "5/8", value: 0.625 },
      { fraction: "3/4", value: 0.75 },
      { fraction: "7/8", value: 0.875 },
    ];

    let closestFraction = fractions[0];
    let minDifference = Math.abs(decimal - closestFraction.value);

    for (const fraction of fractions) {
      const difference = Math.abs(decimal - fraction.value);
      if (difference < minDifference) {
        minDifference = difference;
        closestFraction = fraction;
      }
    }
    return minDifference < 0.0625 ? closestFraction.fraction : ""; // Only return fraction if close enough
  };

  const convertToInches = (value, unit) => {
    let inches;
    switch (unit) {
      case "inches":
        inches = value;
        break;
      case "cm":
        inches = value * 0.393701;
        break;
      case "mm":
        inches = value * 0.0393701;
        break;
      case "feet":
        inches = value * 12;
        break;
      case "yards":
        inches = value * 36;
        break;
      default:
        inches = value;
    }

    const integerPart = Math.floor(inches);
    const decimalPart = inches - integerPart;
    const fraction = convertToFraction(decimalPart);

    // If fraction is empty and decimal is close to 1, increment integer
    const finalInteger = fraction === "" && decimalPart >= 0.9375 ? integerPart + 1 : integerPart;
    return fraction && finalInteger !== integerPart + 1
      ? `${finalInteger} ${fraction}`.trim()
      : `${finalInteger}`;
  };

  const handleAdd = () => {
    if (!width || isNaN(parseFloat(width)) || !height || isNaN(parseFloat(height))) {
      console.error("Invalid width or height input");
      return;
    }

    const widthInInches = convertToInches(parseFloat(width), unit);
    const heightInInches = convertToInches(parseFloat(height), unit);

    setOrder((prevOrder) => {
      const existingEntryIndex = prevOrder.findIndex(
        (entry) => !entry.width || !entry.height
      );

      if (existingEntryIndex !== -1) {
        const updatedOrder = [...prevOrder];
        updatedOrder[existingEntryIndex] = {
          ...updatedOrder[existingEntryIndex],
          width: widthInInches,
          height: heightInInches,
        };
        return updatedOrder;
      } else {
        return [...prevOrder, { width: widthInInches, height: heightInInches }];
      }
    });

    setWidth("");
    setHeight("");
    setShowWidthHeightModal(false); 
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="dropdown mt-3">
          <button
            className="btn btn-outline-primary dropdown-toggle float-end"
            type="button"
            id="settingsDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              color: "white",
            }}
          >
            <i className="bi bi-gear"></i>
            Input Settings
          </button>
          <ul className="dropdown-menu" aria-labelledby="settingsDropdown">
            <li>
              <button className="dropdown-item" onClick={handleSettingsClick}>
                <i className="bi bi-gear-wide"></i> Attribute Settings
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={handleAdditionClick}>
                <i className="bi bi-plus"></i> Addition
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={handleDeductionClick}>
                <i className="bi bi-dash"></i> Deduction
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={handleWidthHeightClick}>
                <i className="bi bi-rulers"></i> Add Width & Height
              </button>
            </li>
          </ul>
        </div>

        {showSettingsModal && (
          <SettingsModal show={showSettingsModal} onClose={handleCloseModal} />
        )}

        {showWidthHeightModal && (
          <div
            className="modal show d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Width & Height</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseWidthHeightModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <label className="form-label">Unit</label>
                  <select className="form-select" value={unit} onChange={handleUnitChange}>
                    <option value="inches">Inches</option>
                    <option value="cm">Centimeters (cm)</option>
                    <option value="mm">Millimeters (mm)</option>
                    <option value="feet">Feet</option>
                    <option value="yards">Yards</option>
                  </select>
                  <input
                    type="number"
                    className="form-control mt-2"
                    placeholder="Width"
                    value={width}
                    onChange={handleWidthChange}
                    step="0.01" // Allow decimal inputs
                  />
                  <input
                    type="number"
                    className="form-control mt-2"
                    placeholder="Height"
                    value={height}
                    onChange={handleHeightChange}
                    step="0.01" // Allow decimal inputs
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleAdd}>
                    Add
                  </button>
                  <button className="btn btn-secondary" onClick={handleCloseWidthHeightModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputSettingsDropdown;